// modify-join-kind planner. Replaces the JOIN keyword chain in-place,
// preserving the user's casing. When the new kind is CROSS-style and the
// existing predicate has an `ON` clause, a second PlannedByteEdit drops the
// predicate bytes (between the join target's end and the join range's
// trimmed end).
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — modify.

import type { JoinKind, PlannedByteEdit, StructuralEdit } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import { deleteEdit, trimTrailingWs } from './applyStructuralEdit.shared';
import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';

const JOIN_KEYWORDS: ReadonlySet<string> = new Set([
  'JOIN', 'APPLY', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS',
  'NATURAL', 'LATERAL', 'STRAIGHT_JOIN',
]);

interface JoinKindRange {
  readonly start: number;
  readonly end: number;
}

// Walk back from a join's range.startOffset (which sits at or just after the
// JOIN keyword chain) and find both the first byte of the chain and the
// trimmed end (just past the last keyword letter). Only commits start
// movement when the previous word is itself a JOIN-chain keyword — so
// trailing whitespace from a non-keyword neighbour (e.g. the FROM target
// `a` before `INNER JOIN`) is NOT consumed.
function findJoinKindRange(source: string, joinRangeStart: number): JoinKindRange {
  let end = joinRangeStart;
  while (end > 0 && /\s/.test(source[end - 1])) end--;
  let start = end;
  while (start > 0) {
    let cursor = start;
    while (cursor > 0 && /\s/.test(source[cursor - 1])) cursor--;
    let w = cursor;
    while (w > 0 && /[A-Za-z_]/.test(source[w - 1])) w--;
    if (w === cursor) break;
    const word = source.slice(w, cursor).toUpperCase();
    if (!JOIN_KEYWORDS.has(word)) break;
    start = w;
  }
  return { start, end };
}

function isCrossStyle(kind: JoinKind): boolean {
  return kind === 'cross' || kind === 'cross-apply' || kind === 'outer-apply';
}

function freshKeyword(kind: JoinKind): string {
  switch (kind) {
    case 'inner': return 'INNER JOIN';
    case 'left': return 'LEFT JOIN';
    case 'right': return 'RIGHT JOIN';
    case 'full': return 'FULL JOIN';
    case 'cross': return 'CROSS JOIN';
    case 'lateral': return 'LATERAL';
    case 'cross-apply': return 'CROSS APPLY';
    case 'outer-apply': return 'OUTER APPLY';
  }
}

// Detect the user's preferred casing by inspecting the existing keyword
// bytes. Lowercase-first → emit lowercase; otherwise emit upper-case.
function preserveCasing(existing: string, fresh: string): string {
  const m = existing.match(/[A-Za-z]/);
  if (!m) return fresh;
  if (m[0] === m[0].toLowerCase()) return fresh.toLowerCase();
  return fresh;
}

export function planJoinKindEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-join-kind' }>,
): readonly PlannedByteEdit[] {
  const joins = ctx.structure.joins ?? [];
  if (edit.index < 0 || edit.index >= joins.length) return [];
  const join = joins[edit.index];
  const { start, end } = findJoinKindRange(ctx.source, join.range.startOffset);
  if (start >= end) return [];
  const existing = ctx.source.slice(start, end);
  const replacement = preserveCasing(existing, freshKeyword(edit.newKind));

  const edits: PlannedByteEdit[] = [{
    range: rangeFromOffsets(ctx.lineIdx, start, end),
    replacement,
    rationale: 'modify-join-kind',
  }];

  // When converting to CROSS-style and an ON predicate is present, drop the
  // bytes from after the join target up to (but not including) the join range's
  // trailing whitespace. The predicate carries the trailing whitespace so we
  // trim back to keep the inter-clause separator alive.
  if (isCrossStyle(edit.newKind) && (join.on?.length ?? 0) > 0) {
    const predicateStart = join.right.range.endOffset;
    const predicateEnd = trimTrailingWs(ctx.source, predicateStart, join.range.endOffset);
    if (predicateEnd > predicateStart) {
      edits.push(deleteEdit(ctx, predicateStart, predicateEnd, 'modify-join-kind:drop-on'));
    }
  }
  return edits;
}
